import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Input } from '../input'
import { mcpUrl } from 'src/config/mcp'
import { InfoHint } from 'src/components/info-hint'

interface BotTokenRevealModalProps {
  /** The raw token secret to reveal, or null when nothing to show. */
  token: string | null
  botName: string
  /** Called once the user confirms they've saved the token. */
  onDismissConfirmed: () => void
}

/**
 * One-time reveal of a freshly minted bot token. Intentionally does NOT close
 * on backdrop click or an X — the raw secret is never retrievable again, so the
 * user must tick the acknowledgement checkbox before "Done" is enabled.
 */
export const BotTokenRevealModal: React.FC<BotTokenRevealModalProps> = ({
  token,
  botName,
  onDismissConfirmed,
}) => {
  const { t } = useTranslation()
  const [justCopied, setJustCopied] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)

  if (!token) return null

  const mcpSnippet = JSON.stringify(
    {
      mcpServers: {
        rapa: {
          type: 'http',
          url: mcpUrl,
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  )

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token)
    setJustCopied(true)
    setTimeout(() => setJustCopied(false), 2000)
  }

  const handleDone = () => {
    setAcknowledged(false)
    setJustCopied(false)
    onDismissConfirmed()
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4"
      data-testid="bot-token-reveal-modal"
    >
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-surface p-6 shadow-xl">
        <div className="border-b border-border pb-4">
          <h3 className="text-xl font-semibold text-text-primary">{t('botTokens.tokenCreated')}</h3>
          <p className="mt-1 text-sm text-text-placeholder">
            {t('botTokens.tokenForLabel')}{' '}
            <span className="font-medium text-text-secondary">{botName}</span>
          </p>
        </div>

        <div className="mt-4 space-y-4">
          <div className="border-warning/40 bg-warning/10 rounded-md border p-3 text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{t('botTokens.storeSecurely')}</span>
          </div>

          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={token}
              className="flex-1 font-mono text-xs"
              onFocus={(e) => e.target.select()}
              data-testid="bot-token-value"
            />
            <Button
              variant="ghost"
              onClick={handleCopy}
              className="shrink-0"
              data-testid="bot-token-copy"
            >
              {justCopied ? (
                <>
                  <CheckIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('common.copied')}
                </>
              ) : (
                <>
                  <ClipboardIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('common.copy')}
                </>
              )}
            </Button>
          </div>

          <div data-testid="bot-token-mcp-snippet">
            <h4 className="text-sm font-medium text-text-primary">
              {t('botTokens.connectMcpTitle')}
              <InfoHint
                text={`${t('botTokens.connectMcpHint')} ${t('botTokens.mcpBotPathsNote')}`}
                className="ml-1.5"
              />
            </h4>
            <pre className="mt-2 overflow-x-auto rounded-md bg-gray-100 p-3 font-mono text-xs text-text-secondary">
              {mcpSnippet}
            </pre>
          </div>

          <label className="flex items-start gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid="bot-token-ack"
            />
            {t('botTokens.savedAcknowledgement')}
          </label>

          <div className="flex justify-end pt-2">
            <Button
              variant="primary"
              onClick={handleDone}
              disabled={!acknowledged}
              data-testid="bot-token-done"
            >
              {t('botTokens.done')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
